import agendamentosService from '@/services/agendamentos'

export default {
  namespaced: true,

  state: {
    agendamentos: [],
    agendamento: null,
    calendario: [],
    meusAgendamentos: [],
    historico: [],
    estatisticas: {},
    loading: false,
    error: null,
    pagination: {
      current_page: 1,
      last_page: 1,
      per_page: 15,
      total: 0,
    },
  },

  getters: {
    agendamentos: state => state.agendamentos,
    agendamento: state => state.agendamento,
    calendario: state => state.calendario,
    meusAgendamentos: state => state.meusAgendamentos,
    historico: state => state.historico,
    estatisticas: state => state.estatisticas,
    loading: state => state.loading,
    error: state => state.error,
    pagination: state => state.pagination,

    // Getters úteis
    agendamentosPorStatus: state => status => state.agendamentos.filter(ag => ag.status === status),

    agendamentosPorData: state => data => state.agendamentos.filter(ag => ag.data_agendamento === data),

    proximosAgendamentos: state => {
      const hoje = new Date().toISOString().split('T')[0]
      return state.agendamentos
        .filter(ag => ag.data_agendamento >= hoje)
        .sort((a, b) => new Date(a.data_agendamento) - new Date(b.data_agendamento))
        .slice(0, 5)
    },
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading
    },

    SET_ERROR(state, error) {
      state.error = error
    },

    CLEAR_ERROR(state) {
      state.error = null
    },

    SET_AGENDAMENTOS(state, { data, pagination }) {
      state.agendamentos = data
      if (pagination) {
        state.pagination = pagination
      }
    },

    SET_AGENDAMENTO(state, agendamento) {
      state.agendamento = agendamento
    },

    ADD_AGENDAMENTO(state, agendamento) {
      state.agendamentos.unshift(agendamento)
      state.pagination.total++
    },

    UPDATE_AGENDAMENTO(state, agendamentoAtualizado) {
      const index = state.agendamentos.findIndex(a => a.id === agendamentoAtualizado.id)
      if (index !== -1) {
        state.agendamentos.splice(index, 1, agendamentoAtualizado)
      }
      if (state.agendamento?.id === agendamentoAtualizado.id) {
        state.agendamento = agendamentoAtualizado
      }
    },

    REMOVE_AGENDAMENTO(state, id) {
      state.agendamentos = state.agendamentos.filter(a => a.id !== id)
      state.pagination.total--
      if (state.agendamento?.id === id) {
        state.agendamento = null
      }
    },

    SET_CALENDARIO(state, calendario) {
      state.calendario = calendario
    },

    SET_MEUS_AGENDAMENTOS(state, agendamentos) {
      state.meusAgendamentos = agendamentos
    },

    SET_HISTORICO(state, historico) {
      state.historico = historico
    },

    SET_ESTATISTICAS(state, estatisticas) {
      state.estatisticas = estatisticas
    },
  },

  actions: {
    async listarAgendamentos({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await agendamentosService.listarAgendamentos(params)
        commit('SET_AGENDAMENTOS', {
          data: response.data || response,
          pagination: response.meta || response.pagination,
        })
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async buscarAgendamento({ commit }, id) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await agendamentosService.buscarAgendamento(id)
        commit('SET_AGENDAMENTO', response.data || response)
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async criarAgendamento({ commit }, dados) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await agendamentosService.criarAgendamento(dados)
        const agendamento = response.data || response
        commit('ADD_AGENDAMENTO', agendamento)
        return agendamento
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async atualizarAgendamento({ commit }, { id, dados }) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await agendamentosService.atualizarAgendamento(id, dados)
        const agendamento = response.data || response
        commit('UPDATE_AGENDAMENTO', agendamento)
        return agendamento
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async excluirAgendamento({ commit }, { id, motivoCancelamento = null }) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        await agendamentosService.excluirAgendamento(id, motivoCancelamento)
        commit('REMOVE_AGENDAMENTO', id)
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async buscarAgendamentosCalendario({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await agendamentosService.buscarAgendamentosCalendario(params)
        commit('SET_CALENDARIO', response.data || response)
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async buscarMeusAgendamentos({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await agendamentosService.buscarMeusAgendamentos(params)
        commit('SET_MEUS_AGENDAMENTOS', response.data || response)
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async atualizarStatusAgendamento({ commit }, { id, status, motivo = null }) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await agendamentosService.atualizarStatusAgendamento(id, status, motivo)
        const agendamento = response.data || response
        commit('UPDATE_AGENDAMENTO', agendamento)
        return agendamento
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async buscarHistoricoAgendamento({ commit }, id) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await agendamentosService.buscarHistoricoAgendamento(id)
        commit('SET_HISTORICO', response.data || response)
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async buscarEstatisticasAgendamentos({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await agendamentosService.buscarEstatisticasAgendamentos(params)
        commit('SET_ESTATISTICAS', response.data || response)
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async buscarAgendamentosPorAtendente({ commit }, { atendenteId, params = {} }) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await agendamentosService.buscarAgendamentosPorAtendente(atendenteId, params)
        return response
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async reagendarAgendamento({ commit }, { id, dados }) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')

      try {
        const response = await agendamentosService.reagendarAgendamento(id, dados)
        const agendamento = response.data || response
        commit('UPDATE_AGENDAMENTO', agendamento)
        return agendamento
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    clearError({ commit }) {
      commit('CLEAR_ERROR')
    },
  },
}
